import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

interface PrivacyPolicyModalProps {
  open: boolean;
  onClose: () => void;
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" scroll="paper">
      <DialogTitle sx={{ fontWeight: "bold" }}>Privacy & Cookie Policy</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body1">
            <strong>Effective Date: April 16, 2026</strong>
          </Typography>
          <Typography variant="body1">
            This policy describes how Obsidian Ridge Holdings, LLC (&quot;Obsidian Ridge,&quot;
            &quot;we,&quot; &quot;us&quot;) collects, uses, and protects information when you use
            our website at obsidianridge.ai and related services. We collect only what is needed to
            operate the platform. We never sell your personal data.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            1. Information We Collect
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Account data.</strong> When you create an account we collect your email address
            and password (hashed by our authentication provider). If you sign in with Google or
            GitHub, we receive the name and email associated with that account.
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Profile data.</strong> After registration you may provide your full name and
            phone number.
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Payment data.</strong> Purchases are processed by Stripe. We never receive or
            store credit card numbers. We retain the transaction amount, currency, Stripe session
            ID, and payment status.
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Contact form data.</strong> If you use our Contact page we collect your name,
            email, phone (optional), company (optional), subject, and message. This data is
            transmitted via EmailJS and is not stored in our database.
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Usage data.</strong> Course enrollments, lesson progress, exam attempts,
            certification records, Signal Desk votes, and bookmarks are stored in your account.
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Analytics data.</strong> With your consent, Google Analytics 4 collects
            anonymized page-view and session data. No personally identifiable information is sent to
            Google Analytics.
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Automated data.</strong> Cloudflare processes standard request metadata (IP
            address, user-agent) as part of content delivery and bot protection. Cloudflare
            Turnstile tokens are used during sign-up to prevent abuse and are not stored by us.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            2. How We Use Your Information
          </Typography>
          <Typography variant="body2">
            We use the information above to: create and maintain your account; provide access to
            courses, exams, and certifications; process payments; respond to support requests;
            protect against fraud and abuse; and improve site performance through aggregated
            analytics.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            3. Third-Party Service Providers
          </Typography>
          <Typography variant="body2" component="div">
            We share data with the following processors, limited to what each needs to perform its
            function:
          </Typography>
          <ul style={{ margin: "8px 0", paddingLeft: "24px" }}>
            <li>
              <Typography variant="body2">
                <strong>Supabase</strong> - authentication, database hosting, and serverless
                functions
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Stripe</strong> - payment processing
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Google Analytics 4</strong> - anonymized website analytics (consent-gated)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>EmailJS</strong> - contact form delivery
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Cloudflare</strong> - content delivery, security headers, and bot protection
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>YouTube</strong> - embedded video content (privacy-enhanced mode)
              </Typography>
            </li>
          </ul>
          <Typography variant="body2">
            We do not sell, rent, or trade your personal information to any third party.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            4. Data Security
          </Typography>
          <Typography variant="body2">
            We use HTTPS everywhere, enforce a Content Security Policy, hash all passwords
            server-side, and restrict access to production data. Payments are handled entirely by
            Stripe so card details never reach our servers. No system is perfectly secure - if you
            suspect unauthorized access to your account, contact us immediately.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            5. Cookies
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Cookies are small text files stored on your device. We use them for authentication,
            preference storage, and - with your consent - analytics. You can manage your choices at
            any time via &quot;Cookie Settings&quot; in the site footer.
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Cookie</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Retention</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>cookie_consent</TableCell>
                  <TableCell>Essential</TableCell>
                  <TableCell>Stores your cookie consent preferences</TableCell>
                  <TableCell>1 year</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>sb-*</TableCell>
                  <TableCell>Essential</TableCell>
                  <TableCell>Authentication session tokens (Supabase)</TableCell>
                  <TableCell>Session / refresh</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>cf_clearance</TableCell>
                  <TableCell>Essential</TableCell>
                  <TableCell>Cloudflare bot-protection challenge</TableCell>
                  <TableCell>Session</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>_ga, _ga_*</TableCell>
                  <TableCell>Analytics</TableCell>
                  <TableCell>Google Analytics 4 - anonymous usage statistics</TableCell>
                  <TableCell>Up to 2 years</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>_gid</TableCell>
                  <TableCell>Analytics</TableCell>
                  <TableCell>Google Analytics 4 - daily visitor distinction</TableCell>
                  <TableCell>24 hours</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            6. Managing & Revoking Consent
          </Typography>
          <Typography variant="body2">
            Click &quot;Cookie Settings&quot; in the site footer to change your preferences at any
            time. When you revoke analytics consent, all Google Analytics cookies are immediately
            deleted from your browser and no further data is sent to Google. You may also clear
            cookies through your browser settings.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            7. Data Retention
          </Typography>
          <Typography variant="body2">
            Account and enrollment data is retained for as long as your account is active. If you
            request account deletion we will remove your personal data within 30 days, except where
            retention is required by law (e.g., financial records).
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            8. Your Rights
          </Typography>
          <Typography variant="body2">
            You may request access to, correction of, or deletion of your personal data at any time.
            You may also request a portable copy of data you have provided. We will respond within
            30 days. To exercise any right, email{" "}
            <Link href="mailto:privacy@obsidianridge.ai">privacy@obsidianridge.ai</Link>.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            9. Children&#39;s Privacy
          </Typography>
          <Typography variant="body2">
            Our services are not directed to individuals under 16. We do not knowingly collect data
            from children. If you believe a child has provided us with personal data, please contact
            us and we will delete it promptly.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            10. Changes to This Policy
          </Typography>
          <Typography variant="body2">
            We may update this policy from time to time. Material changes will be communicated via a
            notice on the site. Continued use after changes take effect constitutes acceptance of
            the updated policy.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            11. Contact
          </Typography>
          <Typography variant="body2">
            For privacy inquiries, email{" "}
            <Link href="mailto:privacy@obsidianridge.ai">privacy@obsidianridge.ai</Link> or use our
            Contact page.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
